import React from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { ReactSelect, SpinnerLoader } from "@services/ui-components";
import { warehouseValidationSchema, WearhousesModalProps } from "./modalsUtils";
import { useCreateStorageMutation } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface AddWarehouseFormValues {
    name: string;
    storageType: number | null;
    description: string;
}

const AddWarehouseModal: React.FC<WearhousesModalProps> = ({
    show,
    handleModal,
    branchId,
    refetch,
    storages,
    toggleShowToast,
}) => {
    const { t } = useTranslation();
    const canCreateWarehouse = useRoleAccess(CompanyAccess.WarehouseCreate);
    const initialValues: AddWarehouseFormValues = {
        name: "",
        storageType: null,
        description: "",
    };
    const [createStorage, { loading }] = useCreateStorageMutation({
        context: {
            errorType: "local",
        },
    });

    const handleSubmit = async (
        values: AddWarehouseFormValues,
        { setFieldError }: FormikHelpers<AddWarehouseFormValues>
    ) => {
        if (!canCreateWarehouse) return;

        try {
            const inputValues = { ...values, description: values?.description.length > 0 ? values.description : null };
            await createStorage({
                variables: {
                    branchId: Number(branchId),
                    input: inputValues,
                },
            });
        } catch (error) {
            const graphQLError = error.graphQLErrors?.[0].extensions.validation[""][0].message;
            if (graphQLError === "The storage already exists") {
                setFieldError("name", t("Warehouse with this name already exists!"));
                return;
            }
        }
        refetch();
        setTimeout(() => handleModal(), 0);
        toggleShowToast(t("Created"));
    };

    return (
        <Modal size="lg" show={show} onHide={handleModal} centered>
            <Modal.Header className="border-0" closeButton>
                <h5 className="m-0"> {t("Create new warehouse")}</h5>
            </Modal.Header>
            <Modal.Body>
                <Formik
                    initialValues={initialValues}
                    validationSchema={warehouseValidationSchema(t)}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                >
                    {({ values, errors, setFieldValue, isSubmitting }) => (
                        <Form>
                            <Row className="m-0">
                                <Col sm={3} className="d-flex align-items-center pr-0">
                                    <label
                                        htmlFor="createWarehouseModal-name-field"
                                        className="text-dark form-label required text-break mb-2 mb-sm-0"
                                    >
                                        <span className="text-dark fw-bold mt-1 fs-7 required-label">
                                            {t("Warehouse name")}
                                        </span>
                                    </label>
                                </Col>
                                <Col sm={9}>
                                    <Field
                                        type="text"
                                        name="name"
                                        placeholder={t("Add storage name")}
                                        className={`fs-7 form-control ${errors.name ? "is-invalid" : ""}`}
                                        maxLength={50}
                                        id="createWarehouseModal-name-field"
                                    />
                                </Col>
                            </Row>
                            <Row className="m-0">
                                <Col sm={3} className="d-flex align-items-center pr-0"></Col>
                                <Col sm={9}>
                                    <span className={`fs-7 ${values?.name?.length > 50 ? "text-danger" : "text-grey"}`}>
                                        {values?.name?.length}/50
                                    </span>
                                    <ErrorMessage name="name" component="div" className="text-danger fs-7" />
                                </Col>
                            </Row>
                            <Row className="pt-3 pt-sm-4 m-0">
                                <Col sm={3} className="d-flex align-items-center pr-0">
                                    <label
                                        htmlFor="addWarehouseModal-storageType-field"
                                        className="text-dark form-label required text-break mb-2 mb-sm-0"
                                    >
                                        <span className="text-dark fw-bold mt-1 fs-7 required-label">
                                            {t("Warehouse type")}
                                        </span>
                                    </label>
                                </Col>
                                <Col sm={9}>
                                    <ReactSelect
                                        id="addWarehouseModal-storageType-field"
                                        name="storageType"
                                        options={storages}
                                        onChange={(option) => setFieldValue("storageType", option.value)}
                                        placeholder={t("Select storage type")}
                                        isSearchable={true}
                                    />
                                    <ErrorMessage name="storageType">
                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                    </ErrorMessage>
                                </Col>
                            </Row>
                            <Row className="pt-3 pt-sm-4 mb-3 mx-0">
                                <Col sm={3}>
                                    <label
                                        htmlFor="createWarehouseModal-description-field"
                                        className="text-dark text-break form-label pt-sm-6 mb-2 mb-sm-0"
                                    >
                                        <span className="text-dark fw-bold fs-7">{t("Description")}</span>
                                    </label>
                                </Col>
                                <Col sm={9}>
                                    <Field
                                        as="textarea"
                                        name="description"
                                        placeholder={t("Add description")}
                                        className={`fs-7 form-control ${errors.description ? "is-invalid" : ""}`}
                                        maxLength={400}
                                        id="createWarehouseModal-description-field"
                                    />
                                    <span
                                        className={`fs-7 ${values?.description?.length > 400 ? "text-danger" : "text-grey"}`}
                                    >
                                        {values?.description?.length}/400
                                    </span>
                                    <ErrorMessage name="description" component="div" className="text-danger fs-7" />
                                </Col>
                            </Row>
                            <Row className="d-flex justify-content-end pt-6">
                                <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                    <Button
                                        variant="outline-primary"
                                        className="fw-normal custom-btn rounded-1"
                                        onClick={handleModal}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                </Col>
                                <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="fw-bold custom-btn rounded-1"
                                        disabled={loading || isSubmitting || !canCreateWarehouse}
                                    >
                                        {loading ? (
                                            <SpinnerLoader variant="light" loadingText={t("Creating...")} />
                                        ) : (
                                            t("Create")
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};

export default AddWarehouseModal;
